import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";

/**
 * Authentication State
 */
interface AuthState {
  isAuthenticated: boolean;
  preSignupToken: string | null;
  preMfaToken: string | null;
  user: User | null;
}

/**
 * User Information
 */
interface User {
  id: string;
  email: string;
  fullName: string;
  companyName: string;
  hasMfa: boolean;
}

/**
 * Auth Context Value
 */
interface AuthContextValue {
  // State
  isAuthenticated: boolean;
  user: User | null;
  preSignupToken: string | null;
  preMfaToken: string | null;

  // Actions
  setPreSignupToken: (token: string | null) => void;
  setPreMfaToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  login: (user: User) => void;
  logout: () => void;
  clearTokens: () => void;
}

/**
 * Create Auth Context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Auth Provider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth Provider Component
 *
 * Manages authentication state in memory
 * Session is managed via HttpOnly cookies on the server
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    preSignupToken: null,
    preMfaToken: null,
    user: null,
  });

  /**
   * Set pre-signup token (after email OTP verification)
   */
  const setPreSignupToken = useCallback((token: string | null) => {
    setAuthState((prev) => ({
      ...prev,
      preSignupToken: token,
    }));
  }, []);

  /**
   * Set pre-MFA token (after password set, before MFA)
   */
  const setPreMfaToken = useCallback((token: string | null) => {
    setAuthState((prev) => ({
      ...prev,
      preMfaToken: token,
    }));
  }, []);

  /**
   * Set user information
   */
  const setUser = useCallback((user: User | null) => {
    setAuthState((prev) => ({
      ...prev,
      user,
      isAuthenticated: user !== null,
    }));
  }, []);

  /**
   * Login (called after successful authentication)
   */
  const login = useCallback((user: User) => {
    setAuthState({
      isAuthenticated: true,
      user,
      preSignupToken: null,
      preMfaToken: null,
    });
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(() => {
    setAuthState({
      isAuthenticated: false,
      preSignupToken: null,
      preMfaToken: null,
      user: null,
    });
  }, []);

  /**
   * Clear all tokens
   */
  const clearTokens = useCallback(() => {
    setAuthState((prev) => ({
      ...prev,
      preSignupToken: null,
      preMfaToken: null,
    }));
  }, []);

  /**
   * Listen for unauthorized events from HTTP client
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);

    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, [logout]);

  const value: AuthContextValue = {
    isAuthenticated: authState.isAuthenticated,
    user: authState.user,
    preSignupToken: authState.preSignupToken,
    preMfaToken: authState.preMfaToken,
    setPreSignupToken,
    setPreMfaToken,
    setUser,
    login,
    logout,
    clearTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Access authentication state and actions
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, logout } = useAuth();
 *
 *   return (
 *     <div>
 *       {isAuthenticated ? (
 *         <button onClick={logout}>Logout</button>
 *       ) : (
 *         <p>Please log in</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}

/**
 * RequireAuth Component
 * Redirect if not authenticated
 */
interface RequireAuthProps {
  children: ReactNode;
  redirectTo?: string;
}

export function RequireAuth({
  children,
  redirectTo = "/signup",
}: RequireAuthProps) {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      window.location.href = redirectTo;
    }
  }, [isAuthenticated, redirectTo]);

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}

/**
 * RequireToken Component
 * Ensure pre-signup or pre-MFA token exists
 */
interface RequireTokenProps {
  children: ReactNode;
  tokenType: "preSignup" | "preMfa";
  redirectTo?: string;
}

export function RequireToken({
  children,
  tokenType,
  redirectTo = "/signup",
}: RequireTokenProps) {
  const { preSignupToken, preMfaToken } = useAuth();

  const hasToken = tokenType === "preSignup" ? preSignupToken : preMfaToken;

  useEffect(() => {
    if (!hasToken) {
      window.location.href = redirectTo;
    }
  }, [hasToken, redirectTo]);

  if (!hasToken) {
    return null;
  }

  return <>{children}</>;
}
